import { useState } from "react";
import {
  OutlinedButton,
  PrimaryButton,
  TextInput,
} from "@normasteaching/norma-ui";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useRefundBonificoOrder } from "../../ProductService/BonificoOrder/useRefundBonificoOrder.ts";
import {
  BonificoOrderDTO,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { useWithdrawalRequestDate } from "../../SpeakingClassService/Withdrawal/useWithdrawalRequestDate.ts";
import { WithdrawalRequestDateField } from "../../SpeakingClassService/Withdrawal/WithdrawalRequestDateField.tsx";

type Props = {
  order: BonificoOrderDTO;
  onSuccess: () => void;
  onClose: () => void;
};

export function RefundBonificoOrderModal({ order, onSuccess, onClose }: Props) {
  const [refundType, setRefundType] = useState<"full" | "partial">("full");
  const [partialAmount, setPartialAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { refundOrder, loading } = useRefundBonificoOrder();
  const withdrawalDate = useWithdrawalRequestDate(order.customer.email);

  const maxRefundableEuros = (order.amountPaidInCents?.amount ?? 0) / 100;

  const handleConfirm = async () => {
    setError(null);

    if (refundType === "partial") {
      const amount = parseFloat(partialAmount);
      if (isNaN(amount) || amount <= 0) {
        setError("Inserisci un importo valido per il rimborso parziale");
        return;
      }
      if (amount > maxRefundableEuros) {
        setError(
          `L'importo non può superare €${maxRefundableEuros.toFixed(2)}`,
        );
        return;
      }
      try {
        await withdrawalDate.persist();
        await refundOrder(order.orderId, Math.round(amount * 100));
        onSuccess();
      } catch (e) {
        setError(
          getBonificoOrderErrorMessage(
            e,
            "Errore durante il rimborso. Riprova.",
          ),
        );
      }
    } else {
      try {
        await withdrawalDate.persist();
        await refundOrder(order.orderId);
        onSuccess();
      } catch (e) {
        setError(
          getBonificoOrderErrorMessage(
            e,
            "Errore durante il rimborso. Riprova.",
          ),
        );
      }
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Rimborso ordine">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Ordine: <span className="font-mono text-xs">{order.orderId}</span>
          <br />
          Cliente:{" "}
          <strong>
            {order.customer.firstName} {order.customer.lastName}
          </strong>{" "}
          — {order.customer.email}
          <br />
          Importo pagato: <strong>€{maxRefundableEuros.toFixed(2)}</strong>
        </p>

        <label
          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
            refundType === "full"
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200"
          }`}
        >
          <input
            type="radio"
            name="refundType"
            value="full"
            checked={refundType === "full"}
            onChange={() => setRefundType("full")}
            className="mt-1 w-4 h-4"
            disabled={loading}
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900">Rimborso Totale</div>
            <div className="text-sm text-gray-600">
              Rimborsa l'intero importo{" "}
              <span className="font-medium">
                (€{maxRefundableEuros.toFixed(2)})
              </span>
            </div>
          </div>
        </label>

        <label
          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
            refundType === "partial"
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200"
          }`}
        >
          <input
            type="radio"
            name="refundType"
            value="partial"
            checked={refundType === "partial"}
            onChange={() => setRefundType("partial")}
            className="mt-1 w-4 h-4"
            disabled={loading}
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900">Rimborso Parziale</div>
            <div className="text-sm text-gray-600">
              Specifica l'importo da rimborsare
            </div>
          </div>
        </label>

        {refundType === "partial" && (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Importo da rimborsare (€)
            </label>
            <TextInput
              type="number"
              placeholder="0.00"
              value={partialAmount}
              onChange={(e) => setPartialAmount(e.target.value)}
              disabled={loading}
              min={0}
              step="0.01"
            />
            <p className="text-xs text-gray-500 mt-1">
              Massimo: €{maxRefundableEuros.toFixed(2)}
            </p>
          </div>
        )}

        <WithdrawalRequestDateField
          date={withdrawalDate.date}
          onChange={withdrawalDate.setDate}
          existingRequest={withdrawalDate.existingRequest}
          hasWithdrawablePurchase={withdrawalDate.hasWithdrawablePurchase}
          loading={withdrawalDate.loading}
          disabled={loading}
        />

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <PrimaryButton
            onClick={handleConfirm}
            label="Conferma rimborso"
            loading={loading}
            disabled={loading}
          />
          <OutlinedButton
            onClick={onClose}
            label="Annulla"
            disabled={loading}
          />
        </div>
      </div>
    </Modal>
  );
}
